'use client'

import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import NextLink from 'next/link'
import { useTranslations } from 'next-intl'
import { Link as I18nLink } from '@/i18n/navigation'
import { useIsI18nContext } from '@/hooks/useIsI18nContext'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination } from 'swiper/modules'
import { adventures } from '@/lib/data'
import { useActivityVisibility } from '@/hooks/useActivityVisibility'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import './Adventures.css'

export default function Adventures() {
  const sectionRef = useScrollAnimation<HTMLElement>({
    threshold: 0.1,
    rootMargin: '0px 0px 150px 0px'
  })
  const isI18n = useIsI18nContext()
  const Link = isI18n ? I18nLink : NextLink

  // Llamar hooks siempre (sin try-catch) - el NextIntlClientProvider garantiza el contexto
  const tHome = useTranslations('homepage.adventuresSection')
  const tAdv = useTranslations('adventures')
  const { isVisible } = useActivityVisibility()

  // Helper para obtener traducciones de adventures con fallback
  const getAdventureTranslation = (adventureId: string, field: string): string => {
    const key = `${adventureId}.${field}`
    const translation = tAdv(key)
    // Si la traduccion devuelve la key, usar el fallback del data
    return translation === key ? '' : translation
  }


  return (
    <section className="adventures" data-animate-on-scroll ref={sectionRef}>
      <div className="container">
        <div className="section-header" data-scroll-item>
          <h2 className="section-title">{tHome('title')}</h2>
          <p className="section-description">{tHome('description')}</p>
        </div>

        <div className="adventures-carousel">
          <Swiper
            modules={[Navigation, Pagination]}
            slidesPerView={1}
            spaceBetween={24}
            loop={true}
            grabCursor={true}
            navigation={{
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            }}
            pagination={{
              el: '.swiper-pagination',
              clickable: true,
              dynamicBullets: true,
            }}
            breakpoints={{
              640: {
                slidesPerView: 2,
                spaceBetween: 24,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 32,
              },
            }}
            className="adventures-swiper"
          >
            {adventures.filter(a => isVisible(a.id, a.hidden)).map((adventure) => {
              const translatedTitle = getAdventureTranslation(adventure.id, 'shortTitle') || adventure.shortTitle || adventure.title
              const translatedDescription = getAdventureTranslation(adventure.id, 'subtitle') || adventure.subtitle || adventure.description

              return (
                <SwiperSlide key={adventure.id}>
                  <article className="adventure-card">
                    <div className="adventure-image">
                      <img src={adventure.heroImage} alt={translatedTitle} loading="lazy" className={`adv-img--${adventure.id}`} />
                    </div>
                    <div className="adventure-overlay">
                      <h3 className="adventure-title">{translatedTitle}</h3>
                      <div className="adventure-content">
                        <p>{translatedDescription}</p>
                        <Link href={adventure.link} className="adventure-link" prefetch={false}>
                          {tHome('explore')} →
                        </Link>
                      </div>
                    </div>
                  </article>
                </SwiperSlide>
              )
            })}
          </Swiper>

          {/* Navigation arrows */}
          <div className="swiper-button-prev"></div>
          <div className="swiper-button-next"></div>

          {/* Pagination */}
          <div className="swiper-pagination"></div>
        </div>

        <div className="section-cta" data-scroll-item>
          <Link href="/guided-adventures" className="btn-primary" prefetch={false}>
            {tHome('cta')} →
          </Link>
        </div>
      </div>
    </section>
  )
}
